import { createSelector } from 'reselect'
import { DateTime } from 'luxon'
import { ReduxState } from '@/utils/typeHelpers'
import {
  YearEndModuleStatusOptions,
  YearEndModuleType,
} from './yearEndModuleStatus.slice'
import { selectIsCatchUpBookkeepingComplete } from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.selector'
import { selectCurrentAnnualTaxDetails } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { convertUtcToLocalDate, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import {
  getYearEndModuleStatuses,
  getYearEndModuleStatusesByType,
} from './baseYearEndModuleStatus.selector'

export const getMostUrgentYearEndModuleType = (state: ReduxState) =>
  state.yearEndModuleStatuses.mostUrgentModule

export const selectYearEndModuleByType = createSelector(
  getYearEndModuleStatusesByType,
  (_: unknown, moduleType: YearEndModuleType) => moduleType,
  (byModuleType, moduleType) =>
    byModuleType[moduleType] ? byModuleType[moduleType] : null
)

export const getCompleteYearEndModuleStatuses = createSelector(
  getYearEndModuleStatuses,
  (modules) =>
    modules.filter(
      (module) => module.status === YearEndModuleStatusOptions.complete
    )
)
export const getIncompleteYearEndModuleStatuses = createSelector(
  getYearEndModuleStatuses,
  (modules) =>
    modules.filter(
      (module) => module.status !== YearEndModuleStatusOptions.complete
    )
)

export { selectAllYearEndModulesComplete } from './baseYearEndModuleStatus.selector'

export const selectOrderedCompletedYEModules = createSelector(
  getCompleteYearEndModuleStatuses,
  (completeModules) => completeModules.sort((a, b) => a.order - b.order)
)
export const selectOrderedIncompleteYEModules = createSelector(
  getIncompleteYearEndModuleStatuses,
  (incompleteModules) => incompleteModules.sort((a, b) => a.order - b.order)
)
export const selectMostUrgentModule = createSelector(
  getYearEndModuleStatusesByType,
  getMostUrgentYearEndModuleType,
  (byModuleType, mostUrgentModule) =>
    mostUrgentModule ? byModuleType[mostUrgentModule] : null
)

export const haveAnyModulesBeenStarted = createSelector(
  getYearEndModuleStatuses,
  (byModuleType) =>
    Object.values(byModuleType).some(
      (module) => module.startedAt || module.completedAt
    )
)

export const getModuleNameCopy = (moduleType: YearEndModuleType) => {
  switch (moduleType) {
    case YearEndModuleType.verifyTaxEntity:
      return 'Verify tax entity status'
    case YearEndModuleType.taxQuestionnaire:
      return 'Complete Tax Questionnaire'
    case YearEndModuleType.fileExtensionRequest:
      return 'File Extension Request'
    case YearEndModuleType.submitExtensionRequest:
      return 'Submit Extension Request'
    case YearEndModuleType.eoyBookkeeping:
      return 'Complete Year-End Bookkeeping'
    case YearEndModuleType.file1099Nec:
      return 'File 1099-NEC through Taxfyle'
    case YearEndModuleType.eoyBookkeepingFollowup:
      return 'Review and close books'
    case YearEndModuleType.file1120S:
      return 'File Business Tax Return (Form 1120-S)'
    case YearEndModuleType.file1040:
      return 'File Personal Tax Return (Form 1040)'
    default:
      return moduleType satisfies never
  }
}

export const getBlockedByText = (
  blockedByModule: YearEndModuleType[],
  isBlockedByOnboarding = false,
  launchDate?: DateTime | null
) => {
  const launchDateText = launchDate?.isValid ? (
    <b>
      on{' '}
      {convertUtcToLocalDate(launchDate.toISO())?.toFormat(
        DATE_FORMATS_LUXON.MONTH_DAY
      )}
    </b>
  ) : (
    ' '
  )
  const baseText = <>Available {launchDateText} after completing</>
  const fallbackText = 'Coming soon'
  const modulesNames = blockedByModule.map((moduleType) =>
    getModuleNameCopy(moduleType)
  )
  const blockingNames: string[] = modulesNames.length > 0 ? modulesNames : []

  if (isBlockedByOnboarding) {
    blockingNames.push('Catch-up bookkeeping')
  }
  if (blockingNames.length === 0) {
    if (launchDate) {
      return <>Available {launchDateText}</>
    }
    return <b>{fallbackText}</b>
  }
  return (
    <>
      {baseText}{' '}
      {blockingNames.length < 3 ? (
        blockingNames.map((name, index) => (
          <span key={name}>
            <b>{name}</b>
            {index < blockingNames.length - 1 ? ' and ' : ''}
          </span>
        ))
      ) : (
        <>
          {blockingNames.slice(0, blockingNames.length - 1).map((name) => (
            <span key={name}>
              <b>{name}</b>,{' '}
            </span>
          ))}
          and <b>{blockingNames[blockingNames.length - 1]}</b>
        </>
      )}
    </>
  )
}

export const selectAvailableAfterTextFor = createSelector(
  getYearEndModuleStatuses,
  selectCurrentAnnualTaxDetails,
  selectIsCatchUpBookkeepingComplete,
  (_: unknown, moduleType: YearEndModuleType) => moduleType,
  (yeModules, currentTaxDetails, isCubkComplete, moduleType) => {
    const blockedByVerifyAndFileExtension = yeModules
      .filter(
        (module) =>
          (module.moduleType === YearEndModuleType.verifyTaxEntity ||
            module.moduleType === YearEndModuleType.fileExtensionRequest) &&
          module.status !== YearEndModuleStatusOptions.complete &&
          module.status !== YearEndModuleStatusOptions.missed &&
          module.status !== YearEndModuleStatusOptions.cancelled
      )
      .map((module) => module.moduleType)
    const blockedByTQAndEOYBKFollowup = yeModules
      .filter(
        (module) =>
          (module.moduleType === YearEndModuleType.taxQuestionnaire ||
            module.moduleType === YearEndModuleType.eoyBookkeepingFollowup) &&
          module.status !== YearEndModuleStatusOptions.complete
      )
      .map((module) => module.moduleType)

    switch (moduleType) {
      case YearEndModuleType.verifyTaxEntity:
        // This module doesn't get blockedBy others:
        return null
      case YearEndModuleType.file1099Nec:
        return getBlockedByText(
          [],
          false,
          currentTaxDetails?.form_1099_nec_start_date
            ? DateTime.fromISO(currentTaxDetails?.form_1099_nec_start_date)
            : undefined
        )
      case YearEndModuleType.submitExtensionRequest: {
        const now = DateTime.local()
        const date = convertUtcToLocalDate(
          currentTaxDetails?.extension_survey_start_date ?? null
        )
        return getBlockedByText([], false, date && now > date ? null : date)
      }

      case YearEndModuleType.eoyBookkeeping: {
        const isBlockedByCubk = !isCubkComplete
        return getBlockedByText(
          blockedByVerifyAndFileExtension,
          isBlockedByCubk,
          currentTaxDetails?.eoy_survey_start_date
            ? DateTime.fromISO(currentTaxDetails?.eoy_survey_start_date)
            : undefined
        )
      }
      case YearEndModuleType.eoyBookkeepingFollowup: {
        const blockedBy = yeModules
          .filter(
            (module) =>
              (module.moduleType === YearEndModuleType.eoyBookkeeping ||
                module.moduleType === YearEndModuleType.fileExtensionRequest) &&
              module.status !== YearEndModuleStatusOptions.complete &&
              module.status !== YearEndModuleStatusOptions.missed &&
              module.status !== YearEndModuleStatusOptions.cancelled
          )
          .map((module) => module.moduleType)
        return getBlockedByText(blockedBy)
      }
      case YearEndModuleType.taxQuestionnaire: {
        const now = DateTime.local()
        const date = convertUtcToLocalDate(
          currentTaxDetails?.form_1120_s_tq_start_date ?? null
        )
        const isBlockedByCubk = !isCubkComplete
        return getBlockedByText(
          blockedByVerifyAndFileExtension,
          isBlockedByCubk,
          date && now > date ? null : date
        )
      }
      case YearEndModuleType.fileExtensionRequest:
        return getBlockedByText([YearEndModuleType.submitExtensionRequest])
      case YearEndModuleType.file1120S: {
        return getBlockedByText(blockedByTQAndEOYBKFollowup)
      }
      case YearEndModuleType.file1040: {
        const form1120s = yeModules.find(
          (module) => module.moduleType === YearEndModuleType.file1120S
        )
        if (form1120s) {
          return getBlockedByText([YearEndModuleType.file1120S])
        }
        return getBlockedByText([...blockedByTQAndEOYBKFollowup])
      }
      default:
        return moduleType satisfies never
    }
  }
)

export const select1099NecYearEndModuleStatus = createSelector(
  getYearEndModuleStatusesByType,
  (byModuleType) => byModuleType[YearEndModuleType.file1099Nec]?.status
)

export const select1099NecYearEndModuleSubstep = createSelector(
  getYearEndModuleStatusesByType,
  (byModuleType) =>
    byModuleType[YearEndModuleType.file1099Nec]?.substepIdentifier
)
